import React, { FC, useEffect, useMemo, useState } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { FadeIn } from '../../../components/motion/index';
import { Radio, RadioGroup } from '../../../components/radio-group/index';
import { SelectedCompanyCurrency } from '../../../components/selected-company-currency/index';
import { SnapshotHooks, useFormat, ValuationHooks } from '../../../hooks/index';
import { toRound } from '../../../utils/get-rounded-number.util';
import { toNumber } from '../../../utils/to-number.util';
import { ActivityOverview } from '../activity-overview';
import { EventsMobileSnapshotPlaceholder } from '../components/events-loading-placeholders';
import { useCompanyIncorporationEvent } from '../hooks/use-company-incorporation-event.hook';
import { GraphVariant, graphVariantMobileMap } from '../variables';
import { EventDateCombobox, EventDateValue } from './event-date-combobox';

type EventsMobileProps = {
  companyId: string;
  secondaryQueriesEnabled?: boolean;
};

type IncorporationStakeholderRow = {
  consideredPay: number;
  id: string;
  name: string;
  percentage: number;
  shares: number;
};

export const EventsMobile: FC<EventsMobileProps> = ({
  companyId,
  secondaryQueriesEnabled = true,
}) => {
  const { format } = useFormat();
  const { snapshots, isLoading: isSnapshotsLoading } = SnapshotHooks.useSnapshots({
    companyId,
    enabled: secondaryQueriesEnabled,
  });
  const { valuation: currentValuation, isLoading: isValuationLoading } = ValuationHooks.useCurrent({
    companyId,
    enabled: secondaryQueriesEnabled,
  });
  const { companyIncorporationEvent, isLoading: isIncorporationLoading } =
    useCompanyIncorporationEvent({
      companyId,
      enabled: secondaryQueriesEnabled,
    });

  const isSnapshotLoading =
    !secondaryQueriesEnabled || isSnapshotsLoading || isValuationLoading || isIncorporationLoading;

  const [selectedGraphVariant, setGraphVariant] = useState(GraphVariant.VALUATION);
  const [selectedSnapshot, setSelectedSnapshot] = useState<EventDateValue | null>(null);

  const currentSnapshot = snapshots.find(
    (snapshot) => snapshot.valuation?.id === selectedSnapshot?.id,
  );
  const isCompanyIncorporationSelected = Boolean(selectedSnapshot?.isCompanyIncorporation);

  const sortedByDateSnapshots = [...snapshots]
    .filter(
      (
        snapshot,
      ): snapshot is { valuation: NonNullable<typeof snapshot.valuation> } & typeof snapshot =>
        Boolean(snapshot.valuation),
    )
    .sort((a, b) => {
      const dateA = new Date(a.valuation.date).getTime();
      const dateB = new Date(b.valuation.date).getTime();

      if (dateA === dateB) {
        return b.postMoneyValuation - a.postMoneyValuation;
      }

      return dateB - dateA;
    });

  const incorporationDateOption = companyIncorporationEvent
    ? { id: companyIncorporationEvent.id, date: companyIncorporationEvent.date }
    : null;

  const incorporationSummary = useMemo(() => {
    const items = companyIncorporationEvent?.items;
    if (!companyIncorporationEvent || !Array.isArray(items)) return null;

    const parValue = toNumber(companyIncorporationEvent.parValue) ?? 0;
    const totalIssuedShares = items.reduce((sum, item) => sum + item.sharesCount, 0);
    const stakeholdersById = new Map<string, IncorporationStakeholderRow>();

    items.forEach((item) => {
      if (!item.stakeholder?.id) return;

      const existing = stakeholdersById.get(item.stakeholder.id);
      const shares = item.sharesCount;
      const consideredPay = shares * parValue;

      if (existing) {
        existing.shares += shares;
        existing.consideredPay += consideredPay;
        existing.percentage =
          totalIssuedShares > 0 ? (existing.shares * 100) / totalIssuedShares : 0;
        return;
      }

      stakeholdersById.set(item.stakeholder.id, {
        id: item.stakeholder.id,
        name: item.stakeholder.fullName,
        shares,
        consideredPay,
        percentage: totalIssuedShares > 0 ? (shares * 100) / totalIssuedShares : 0,
      });
    });

    return {
      date: companyIncorporationEvent.date,
      parValue: companyIncorporationEvent.parValue,
      totalIssuedShares,
      totalConsideredPay: totalIssuedShares * parValue,
      stakeholders: Array.from(stakeholdersById.values()).sort((a, b) => b.shares - a.shares),
    };
  }, [companyIncorporationEvent]);

  useEffect(() => {
    if (currentValuation) {
      setSelectedSnapshot({
        id: currentValuation.id,
        date: currentValuation.date,
      });
      return;
    }

    if (companyIncorporationEvent) {
      setSelectedSnapshot({
        id: companyIncorporationEvent.id,
        date: companyIncorporationEvent.date,
        isCompanyIncorporation: true,
      });
    }
  }, [companyIncorporationEvent, currentValuation]);

  const hasSelectedContent =
    selectedSnapshot?.id &&
    selectedSnapshot.date &&
    (isCompanyIncorporationSelected ? Boolean(incorporationSummary) : Boolean(currentSnapshot));

  const thirdColumnLabel =
    selectedGraphVariant === GraphVariant.VALUATION ? (
      isCompanyIncorporationSelected ? (
        <AppFormattedMessage id="CONSIDERED_PAY" />
      ) : (
        <AppFormattedMessage id="VALUE" />
      )
    ) : (
      <AppFormattedMessage id="SHARES_PERCENT" />
    );

  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-white p-3 shadow-sm">
      <ActivityOverview companyId={companyId} />
      <div className="rounded-3xl border border-gray-200 p-1">
        <RadioGroup
          onChange={setGraphVariant}
          value={selectedGraphVariant}
          wrapperClassName="w-full grow flex-1"
        >
          {graphVariantMobileMap.map(([graphKey, graphTitle]) => (
            <Radio
              className="h-8 rounded-[32px] border-transparent bg-transparent !px-3 !py-2 text-xs font-[450] text-gray-700 data-[checked]:font-[550] data-[checked]:text-brand-25"
              key={graphKey}
              value={graphKey}
            >
              <span className="text-nowrap">{graphTitle}</span>
            </Radio>
          ))}
        </RadioGroup>
      </div>
      {isSnapshotLoading ? (
        <EventsMobileSnapshotPlaceholder />
      ) : (
        hasSelectedContent &&
        selectedSnapshot && (
          <FadeIn className="flex w-full flex-col gap-2 rounded-lg p-2 shadow-sm">
            <EventDateCombobox
              companyIncorporation={incorporationDateOption}
              defaultData={selectedSnapshot}
              onSelect={(state) => setSelectedSnapshot(state)}
              snapshots={sortedByDateSnapshots}
            />
            {isCompanyIncorporationSelected && incorporationSummary ? (
              <>
                <div className="flex w-full gap-2">
                  <div className="flex w-full flex-col gap-2 rounded-lg bg-brand-25 px-2 py-3">
                    <span className="font-semibold leading-4 text-[10] text-gray-700">
                      <AppFormattedMessage id="INCORPORATION" />
                    </span>
                    <span className="text-sm font-[450] text-gray-700">
                      {format(incorporationSummary.date, 'dd/MM/yyyy')}
                    </span>
                  </div>
                  <div className="flex w-full flex-col gap-2 rounded-lg bg-forest-25 px-2 py-3">
                    <span className="font-semibold leading-4 text-[10] text-gray-700">
                      <AppFormattedMessage id="PAR_VALUE" />
                    </span>
                    <span className="flex text-sm font-[450] text-forest-700">
                      <SelectedCompanyCurrency />
                      {incorporationSummary.parValue}
                    </span>
                  </div>
                </div>
                <div className="flex w-full gap-2">
                  <div className="flex w-full flex-col gap-2 rounded-lg bg-gray-50 px-2 py-3">
                    <span className="font-semibold leading-4 text-[10] text-gray-700">
                      <AppFormattedMessage id="TOTAL_ISSUED_SHARES" />
                    </span>
                    <span className="text-sm font-[450] text-gray-700">
                      {incorporationSummary.totalIssuedShares.toLocaleString('en-US')}
                    </span>
                  </div>
                  <div className="flex w-full flex-col gap-2 rounded-lg bg-gray-50 px-2 py-3">
                    <span className="font-semibold leading-4 text-[10] text-gray-700">
                      <AppFormattedMessage id="TOTAL_CONSIDERED_PAY" />
                    </span>
                    <span className="flex text-sm font-[450] text-gray-700">
                      <SelectedCompanyCurrency />
                      {incorporationSummary.totalConsideredPay.toLocaleString('en-US', {
                        maximumFractionDigits: 7,
                      })}
                    </span>
                  </div>
                </div>
                <div className="w-full overflow-x-auto">
                  <table className="w-full overflow-hidden rounded-md">
                    <thead className="h-9 rounded-t-md border-b border-[#F2F2F2] bg-[#F9FAFB]">
                      <tr>
                        <th className="px-4 text-left">
                          <span className="text-label-md font-medium text-[#172335]">
                            <AppFormattedMessage id="SHAREHOLDER" />
                          </span>
                        </th>
                        <th className="px-4 text-left">
                          <span className="text-label-md font-medium text-[#172335]">
                            <AppFormattedMessage id="SHARES" />
                          </span>
                        </th>
                        <th className="px-4 text-left">
                          <span className="text-label-md font-medium text-[#172335]">
                            {thirdColumnLabel}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {incorporationSummary.stakeholders
                        .slice(0, selectedGraphVariant === GraphVariant.VALUATION ? 5 : 10)
                        .filter((stakeholder) => stakeholder.shares !== 0)
                        .map((stakeholder) => (
                          <tr key={stakeholder.id}>
                            <td className="max-w-[120px] overflow-hidden truncate px-4 py-3">
                              <span className="truncate text-sm font-[450] text-gray-700">
                                {stakeholder.name}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="max-w-[120px] overflow-hidden truncate">
                                <span className="text-sm font-[450] text-gray-700">
                                  {stakeholder.shares.toLocaleString('en-US')}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="max-w-[120px] overflow-hidden truncate">
                                <span className="text-sm font-[450] text-gray-700">
                                  {selectedGraphVariant === GraphVariant.VALUATION ? (
                                    <span className="flex">
                                      <SelectedCompanyCurrency />
                                      {stakeholder.consideredPay.toLocaleString('en-US', {
                                        maximumFractionDigits: 7,
                                      })}
                                    </span>
                                  ) : (
                                    `${Math.round(stakeholder.percentage)}%`
                                  )}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              currentSnapshot && (
                <>
                  <div className="flex w-full gap-2">
                    <div className="flex w-full flex-col gap-2 rounded-lg bg-brand-25 px-2 py-3">
                      <span className="font-semibold leading-4 text-[10] text-gray-700">
                        {currentSnapshot?.valuation?.name}
                      </span>
                      <span className="text-sm font-[450] text-gray-700">
                        {format(
                          currentSnapshot?.valuation?.date
                            ? currentSnapshot.valuation.date
                            : new Date(),
                          'dd/MM/yyyy',
                        )}
                      </span>
                    </div>
                    <div className="flex w-full flex-col gap-2 rounded-lg bg-forest-25 px-2 py-3">
                      <span className="font-semibold leading-4 text-[10] text-gray-700">
                        <AppFormattedMessage id="POST_MONEY_VALUATION" />
                      </span>
                      <span className="text-sm font-[450] text-forest-700">
                        {toNumber(toRound(currentSnapshot?.postMoneyValuation, 2))?.toLocaleString(
                          'en-US',
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full overflow-hidden rounded-md">
                      <thead className="h-9 rounded-t-md border-b border-[#F2F2F2] bg-[#F9FAFB]">
                        <tr>
                          <th className="px-4 text-left">
                            <span className="text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="SHAREHOLDER" />
                            </span>
                          </th>
                          <th className="px-4 text-left">
                            <span className="text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id="SHARES" />
                            </span>
                          </th>
                          <th className="px-4 text-left">
                            <span className="text-label-md font-medium text-[#172335]">
                              {thirdColumnLabel}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {currentSnapshot.stats.stakeholders
                          .sort((a, b) => b.issued - a.issued)
                          .slice(0, selectedGraphVariant === GraphVariant.VALUATION ? 5 : 10)
                          .filter((stakeholder) => stakeholder.issued !== 0)
                          .map((stakeholder) => (
                            <tr key={stakeholder.id}>
                              <td className="max-w-[120px] overflow-hidden truncate px-4 py-3">
                                <span className="truncate text-sm font-[450] text-gray-700">
                                  {stakeholder.name}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="max-w-[120px] overflow-hidden truncate">
                                  <span className="text-sm font-[450] text-gray-700">
                                    {stakeholder.issued.toLocaleString('en-US')}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="max-w-[120px] overflow-hidden truncate">
                                  <span className="text-sm font-[450] text-gray-700">
                                    {selectedGraphVariant === GraphVariant.VALUATION
                                      ? toNumber(
                                          toRound(
                                            stakeholder.issued *
                                              (currentSnapshot.valuation?.sharePrice || 0),
                                            2,
                                          ),
                                        ).toLocaleString('en-US')
                                      : `${Math.round(stakeholder.issuedPercentage * 100)}%`}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )
            )}
          </FadeIn>
        )
      )}
    </div>
  );
};
